/* global process */

import { MAX_AUTH_FAILS } from '../constants'
import SOUNDS from '../sounds'

import { isFunction, uniqueId } from './utils'

const BUILDING_UPGRADED_NOTIFICATION_ID = '#system_messages:fortification/modernizationBuilding'

const TYPES_WITH_HEADER = {
    ErrorSimple: 'ErrorHeader',
    FinancialTransactionWithGold: 'FinancialTransactionWithGoldHeader',
    Warning: 'WarningHeader',
}

const RESPONSE_TIMEOUT = 4000
const MEMBERS_STATUS_ACTION = 'members_status'
const FRIENDS_STATUS_ACTION = 'friends_status'
const CLAN_MANAGEMENT_COMMAND = 'clan_management'
const CONTEXT_MENU_COMMAND = 'context_menu'

const GET_TOKEN_COMMAND_ID = 'request'
const GET_TOKEN_REQUEST_ID = 'token1'
const GET_ACCESS_TOKEN_REQUEST_ID = 'access_token'
const WoTClientMessageCallbacks = {}

export function defaultWoTClientMessageHandler (callbackString) {
    const callbackData = JSON.parse(callbackString)
    const webId = callbackData.web_id
    const callback = WoTClientMessageCallbacks[webId]
    if (callback) {
        callback(callbackData)
    }
}

export const sendCommand = data => {
    if (!isFunction(window.jsHostQuery)) {
        if (process.env.VERBOSE) {
            /* eslint-disable no-console */
            console.log('window.jsHostQuery does not exist, but it called with', data)
        }
        return
    }
    return window.jsHostQuery({
        request: JSON.stringify(data),
        persistent: false,
        onSuccess: () => null,
        onFailure: () => null,
    })
}

export const getAccessToken = (force) => new Promise((resolve, reject) => {
    const webId = uniqueId(GET_ACCESS_TOKEN_REQUEST_ID)
    const commandData = {
        command: GET_TOKEN_COMMAND_ID,
        web_id: webId,
        params: {
            request_id: GET_ACCESS_TOKEN_REQUEST_ID,
            force: !!force,
        },
    }
    WoTClientMessageCallbacks[webId] = (result) => {
        if (!result.data) {
            reject('no data')
        } else if (result.data.error) {
            reject(result.data.error)
        } else {
            const { access_token, spa_id, expires_in, periphery_id } = result.data
            resolve({
                token: access_token,
                spaId: spa_id,
                expiresIn: expires_in,
                peripheryId: periphery_id,
            })
            WoTClientMessageCallbacks[webId] = null
        }
    }
    sendCommand(commandData)
})

export const getToken1 = () => new Promise((resolve, reject) => {
    const webId = uniqueId(GET_TOKEN_REQUEST_ID)
    const commandData = {
        command: GET_TOKEN_COMMAND_ID,
        web_id: webId,
        params: {
            request_id: GET_TOKEN_REQUEST_ID,
        },
    }
    let failures = 0

    WoTClientMessageCallbacks[webId] = (result) => {
        const { token, spa_id, error, cooldown } = result.data
        if (token && spa_id) {
            resolve({ spaId: spa_id, token1: token })
            WoTClientMessageCallbacks[webId] = null
            return
        }

        failures++
        if (failures === MAX_AUTH_FAILS) {
            reject(error)
            WoTClientMessageCallbacks[webId] = null
            return
        }
        setTimeout(sendCommand.bind(null, commandData), cooldown * 1000 + 100)
    }
    sendCommand(commandData)
})

export const userContextMenu = (id, name, customItems=[], excludedItems=[]) => {
    return new Promise((resolve) => {
        const webId = uniqueId(CONTEXT_MENU_COMMAND)
        sendCommand({
            command: CONTEXT_MENU_COMMAND,
            webId: CONTEXT_MENU_COMMAND,
            params: {
                menu_type: 'user_menu',
                spa_id: id,
                user_name: name,
                custom_items: customItems,
                excluded_items: excludedItems,
            },
        })
        WoTClientMessageCallbacks[webId] = (callbackData) => {
            const selectedItem = callbackData.data && callbackData.data.selected_item
            resolve(selectedItem)
            WoTClientMessageCallbacks[webId] = null
        }
    })
}

const clanManagementAction = (action) => {
    // author your abstraction leaks here :)
    if (!isFunction(window.jsHostQuery)) return Promise.resolve({})
    return new Promise((resolve) => {
        const webId = uniqueId(action)
        sendCommand({
            command: CLAN_MANAGEMENT_COMMAND,
            web_id: webId,
            params: { action },
        })
        WoTClientMessageCallbacks[webId] = (callbackData) => {
            resolve(callbackData.data && callbackData.data[action])
            WoTClientMessageCallbacks[webId] = null
        }
        setTimeout(() => {
            WoTClientMessageCallbacks[webId] = null
            resolve(null)
        }, RESPONSE_TIMEOUT)
    })
}

export const clanPlayersOnlineStatus = () => {
    return clanManagementAction(MEMBERS_STATUS_ACTION)
}

export const playerFriendsOnlineStatus = () => {
    return clanManagementAction(FRIENDS_STATUS_ACTION)
}

export const getSoundCommand = (soundId) => {
    return {
        command: 'sound',
        params: {
            sound_id: soundId,
        },
    }
}

export const openExternalBrowser = (url) => {
    openWindow('external_browser', { url })
}

export const sendUtilCommand = (action, extraParams={}) => {
    sendCommand({
        command: 'util',
        params: {
            action,
            ...extraParams,
        },
    })
}

export const sendOpenTabCommand = (tabId, extraParams={}) => {
    sendCommand({
        command: 'open_tab',
        params: {
            tab_id: tabId,
            ...extraParams,
        },
    })
}

export const blinkTaskbar = () => {
    sendUtilCommand('blink_taskbar')
}

export const addVehicleToComparison = (vehicleId) => {
    sendUtilCommand('vehicle_add_to_comparison', {
        vehicle_id: vehicleId,
    })
}

export const sendNotification = (type, message, header, extraParams={}) => {
    const params = {
        type,
        message,
        ...extraParams,
    }
    if (header) {
        const typeWithHeader = TYPES_WITH_HEADER[type]
        if (!typeWithHeader) {
            const types = Object.keys(TYPES_WITH_HEADER).join(', ')
            throw `Header is only supported for the following types: ${types}`
        }
        params.type = typeWithHeader
        params.message_data = {
            header,
        }
    }
    sendCommand({
        command: 'notification',
        params,
    })
}

export const strongholdBuildingUpgraded = (buildingName, buildingLevel) => {
    playSound(SOUNDS.BUILDING_UPGRADED)
    sendNotification('Warning', void 0, void 0, {
        key: BUILDING_UPGRADED_NOTIFICATION_ID,
        buildingName,
        buildingLevel,
    })
}

export const sendErrorNotification = (errorMessage, statusCode) => {
    if (!errorMessage) return

    const type = statusCode === 412 ? 'Information' : 'Error'
    sendNotification(type, errorMessage)
}

export const openWindow = (windowId, extraParams={}) => {
    sendCommand({
        command: 'open_window',
        params: {
            window_id: windowId,
            ...extraParams,
        },
    })
}

export const closeWindow = (windowId) => {
    sendCommand({
        command: 'close_window',
        params: {
            window_id: windowId,
        },
    })
}

export const openBrowser = (url, title, width, height, isModal, showCreateWaiting, showRefresh = false) => {
    openWindow('browser', {
        show_refresh: showRefresh,
        url,
        title,
        width,
        height,
        is_modal: isModal,
        show_create_waiting: showCreateWaiting,
    })
}

export const openStrongholdsBattleRoom = () => {
    sendCommand({
        command: 'strongholds_battle',
        params: {
            action: 'open_list',
        },
    })
}

export const closeLeaderboard = () => {
    sendCommand({
        command: 'ranked_battles',
        params: {
            action: 'close_browser',
        },
    })
}

export const strongholdsBattleChosen = () => {
    sendCommand({
        command: 'strongholds_battle',
        params: {
            action: 'battle_chosen',
        },
    })
}

/**
 * Opens hangar at the first vehicle of a user or at one provided in the parameter
 *
 * @param {number|string} [vehicleId] - ID of vehicle to open in hangar (optional)
 */
export const openHangar = (vehicleId) => {
    sendOpenTabCommand('hangar', {
        vehicle_id: vehicleId,
    })
}

export const openVehiclePreview = (vehicleId, extraParams={}) => {
    sendOpenTabCommand('vehicle_preview', {
        vehicle_id: vehicleId,
        ...extraParams,
    })
}

export const openVehicleResearch = (vehicleId, extraParams={}) => {
    sendOpenTabCommand('research', {
        vehicle_id: vehicleId,
        ...extraParams,
    })
}

export const openVehicleSell = (vehicleId, extraParams={}) => {
    openWindow('vehicle_sell', {
        vehicle_id: vehicleId,
        ...extraParams,
    })
}

export const openTechTree = (vehicleId, extraParams={}) => {
    sendOpenTabCommand('tech_tree', {
        vehicle_id: vehicleId,
        ...extraParams,
    })
}

export const joinToStrongholdsBattleOnOtherPeriphery = (unitServerId, peripheryId) => {
    sendCommand({
        command: 'strongholds_battle',
        params: {
            action: 'join_battle',
            unit_id: unitServerId,
            periphery_id: peripheryId,
        },
    })
}

export const playSound = (soundId) => sendCommand(getSoundCommand(soundId))

export const sendButtonClickSoundCommand = (soundId = SOUNDS.BUTTON_CLICK) => playSound(soundId)
export const sendCancelClickSoundCommand = playSound.bind(null, SOUNDS.CANCEL_CLICK)
export const sendClickSoundCommand = playSound.bind(null, SOUNDS.CLICK)
export const sendHoverSoundCommand = playSound.bind(null, SOUNDS.HOVER)
export const sendSelectClickSoundCommand = playSound.bind(null, SOUNDS.SELECT_CLICK)
export const sendTabClickSoundCommand = playSound.bind(null, SOUNDS.TAB_CLICK)

if (process.env.NODE_ENV === 'development') {
    window.CommonWoTClientMessageCallbacks = WoTClientMessageCallbacks
}



// WEBPACK FOOTER //
// ../node_modules/react-wot-bootstrap/lib/helpers/web_to_client.js